import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["doesNotMeetMAUCriteria", "attributes"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useCallback, useContext } from 'react';
import { match, P } from 'ts-pattern';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { AIEventsInstrumentationContext } from '../context';
import { AISessionState } from '../events';
export var useAIEventsInstrumentation = function useAIEventsInstrumentation() {
  var context = useContext(AIEventsInstrumentationContext);
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  if (context === null) {
    throw new Error('useAIEventsInstrumentation must be used within a AIEventsInstrumentationProvider');
  }
  var _context$config = context.config,
    source = _context$config.source,
    aiExperienceName = _context$config.aiExperienceName,
    aiFeatureName = _context$config.aiFeatureName,
    proactiveGeneratedAI = _context$config.proactiveGeneratedAI,
    userGeneratedAI = _context$config.userGeneratedAI,
    isAIFeature = _context$config.isAIFeature,
    channel = _context$config.channel,
    subproduct = _context$config.subproduct,
    actionSubjectId = _context$config.actionSubjectId,
    tags = _context$config.tags,
    product = _context$config.product,
    eventDispatcherSource = _context$config.eventDispatcherSource,
    skipAISessionValidation = _context$config.skipAISessionValidation,
    customAnalyticsFire = _context$config.customAnalyticsFire,
    additionalAttributes = _context$config.additionalAttributes,
    useDefaultGASv3EventSchema = _context$config.useDefaultGASv3EventSchema,
    refreshSingleInstrumentationID = context.refreshSingleInstrumentationID,
    getSingleInstrumentationID = context.getSingleInstrumentationID,
    setAISessionState = context.setAISessionState,
    getAISessionState = context.getAISessionState;
  var validateAndUpdateAISession = useCallback(function (nextState) {
    var currentAISessionState = getAISessionState();
    return match({
      currentState: currentAISessionState,
      nextState: nextState,
      skipAISessionValidation: skipAISessionValidation
    }).with({
      skipAISessionValidation: true
    }, function () {
      return true;
    }).with({
      currentState: AISessionState.unset,
      nextState: P.union(AISessionState.viewed, AISessionState.error, AISessionState.dismissed)
    }, function () {
      // eslint-disable-next-line no-console
      console.error("[@atlassian/ai-analytics]: ".concat(nextState, " event should be called only after trackAIInteractionInit()"));
      return false;
    }).with({
      currentState: AISessionState.initiated,
      nextState: P.union(AISessionState.submitted, AISessionState.actioned)
    }, function () {
      // eslint-disable-next-line no-console
      console.error("[@atlassian/ai-analytics]: ".concat(nextState, " event should be called only after trackAIResultView() OR trackAIResultError() OR trackAIInteractionDismiss() event"));
      return false;
    }).otherwise(function () {
      setAISessionState(nextState);
      return true;
    });
  }, [setAISessionState, getAISessionState, skipAISessionValidation]);
  var fireEventWithOptionalChannel = useCallback(function (event) {
    if (typeof customAnalyticsFire === 'function') {
      customAnalyticsFire(event, createAnalyticsEvent({}));
      return;
    }
    if (channel) {
      createAnalyticsEvent(event).fire(channel);
    } else {
      createAnalyticsEvent(event).fire();
    }
  }, [channel, customAnalyticsFire, createAnalyticsEvent]);
  var createAndMergeEventPayload = useCallback(function (payload, partialPayload) {
    var _partialPayload$actio;
    var singleInstrumentationID = getSingleInstrumentationID();
    var eventPayload = {
      action: payload.action,
      actionSubject: payload.actionSubject,
      actionSubjectId: (_partialPayload$actio = partialPayload === null || partialPayload === void 0 ? void 0 : partialPayload.actionSubjectId) !== null && _partialPayload$actio !== void 0 ? _partialPayload$actio : actionSubjectId,
      attributes: _objectSpread(_objectSpread(_objectSpread({
        aiExperienceName: aiExperienceName,
        aiFeatureName: aiFeatureName,
        proactiveGeneratedAI: proactiveGeneratedAI,
        userGeneratedAI: userGeneratedAI,
        isAIFeature: isAIFeature,
        eventDispatcherSource: eventDispatcherSource,
        singleInstrumentationID: singleInstrumentationID
      }, payload.attributes), partialPayload === null || partialPayload === void 0 ? void 0 : partialPayload.attributes), additionalAttributes)
    };
    return match({
      product: product,
      channel: channel,
      useDefaultGASv3EventSchema: useDefaultGASv3EventSchema
    }).with({
      product: 'confluence',
      channel: P.nullish,
      useDefaultGASv3EventSchema: false
    }, function () {
      var _partialPayload$tags, _partialPayload$sourc;
      // confluence event validation fronted logic
      // https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/browse/jira/src/packages/platform/observability/product-analytics-bridge/src/utils/fire-analytics.tsx#83
      // it requires to use `data` inside event shape and only following event types
      // https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/browse/confluence/next/packages/analytics/src/analyticsListener.tsx#11
      return {
        type: 'sendTrackEvent',
        data: _objectSpread(_objectSpread({}, eventPayload), {}, {
          tags: (_partialPayload$tags = partialPayload === null || partialPayload === void 0 ? void 0 : partialPayload.tags) !== null && _partialPayload$tags !== void 0 ? _partialPayload$tags : tags,
          source: (_partialPayload$sourc = partialPayload === null || partialPayload === void 0 ? void 0 : partialPayload.source) !== null && _partialPayload$sourc !== void 0 ? _partialPayload$sourc : source,
          subproduct: subproduct
        })
      };
    }).with({
      product: 'jira',
      channel: P.nullish,
      useDefaultGASv3EventSchema: false
    }, function () {
      // jira event validation fronted logic
      // https://stash.atlassian.com/projects/ATLASSIAN/repos/atlassian-frontend-monorepo/browse/jira/src/packages/platform/observability/product-analytics-bridge/src/utils/fire-analytics.tsx#83
      // it requires to use no more than 4 properties on the top level of event shape
      return eventPayload;
    }).otherwise(function () {
      var _partialPayload$tags2, _partialPayload$sourc2;
      // for any other product type we should use the default event shape
      // also if there is a custom event channel, we also should use the default event shape
      return _objectSpread(_objectSpread({
        eventType: 'track'
      }, eventPayload), {}, {
        tags: (_partialPayload$tags2 = partialPayload === null || partialPayload === void 0 ? void 0 : partialPayload.tags) !== null && _partialPayload$tags2 !== void 0 ? _partialPayload$tags2 : tags,
        source: (_partialPayload$sourc2 = partialPayload === null || partialPayload === void 0 ? void 0 : partialPayload.source) !== null && _partialPayload$sourc2 !== void 0 ? _partialPayload$sourc2 : source,
        subproduct: subproduct,
        product: product
      });
    });
  }, [subproduct, channel, aiExperienceName, aiFeatureName, product, proactiveGeneratedAI, getSingleInstrumentationID, useDefaultGASv3EventSchema, userGeneratedAI, isAIFeature, source, tags, eventDispatcherSource, actionSubjectId, additionalAttributes]);
  var trackAIInteractionInit = useCallback(function (payload) {
    var _payload$attributes;
    if (!validateAndUpdateAISession(AISessionState.initiated)) {
      return;
    }
    refreshSingleInstrumentationID(payload === null || payload === void 0 || (_payload$attributes = payload.attributes) === null || _payload$attributes === void 0 ? void 0 : _payload$attributes.singleInstrumentationID);
    var event = createAndMergeEventPayload({
      action: 'initiated',
      actionSubject: 'aiInteraction'
    }, payload);
    fireEventWithOptionalChannel(event);
  }, [refreshSingleInstrumentationID, createAndMergeEventPayload, fireEventWithOptionalChannel, validateAndUpdateAISession]);
  var trackAIResultView = useCallback(function (payload) {
    if (!validateAndUpdateAISession(AISessionState.viewed)) {
      return;
    }
    var _ref = payload || {},
      doesNotMeetMAUCriteria = _ref.doesNotMeetMAUCriteria,
      attributes = _ref.attributes,
      payloadRest = _objectWithoutProperties(_ref, _excluded);
    var event = createAndMergeEventPayload({
      action: 'viewed',
      actionSubject: 'aiResult'
    }, _objectSpread(_objectSpread({}, payloadRest), {}, {
      attributes: _objectSpread(_objectSpread({}, attributes), {}, {
        doesNotMeetMAUCriteria: !!doesNotMeetMAUCriteria
      })
    }));
    fireEventWithOptionalChannel(event);
  }, [validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel]);
  var trackAIResultError = useCallback(function (error, payload) {
    if (!validateAndUpdateAISession(AISessionState.error)) {
      return;
    }
    var event = createAndMergeEventPayload({
      action: 'error',
      actionSubject: 'aiResult',
      attributes: _objectSpread({}, error)
    }, payload);
    fireEventWithOptionalChannel(event);
  }, [validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel]);
  var trackAIInteractionDismiss = useCallback(function (payload) {
    if (!validateAndUpdateAISession(AISessionState.dismissed)) {
      return;
    }
    var event = createAndMergeEventPayload({
      action: 'dismissed',
      actionSubject: 'aiInteraction'
    }, payload);
    fireEventWithOptionalChannel(event);
  }, [validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel]);
  var trackAIResultAction = useCallback(function (aiResultAction, payload) {
    if (!validateAndUpdateAISession(AISessionState.actioned)) {
      return;
    }
    var event = createAndMergeEventPayload({
      action: 'actioned',
      actionSubject: 'aiResult',
      attributes: {
        aiResultAction: aiResultAction
      }
    }, payload);
    fireEventWithOptionalChannel(event);
  }, [validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel]);
  var trackAIFeedbackSubmit = useCallback(function (aiFeedbackResult, payload) {
    if (!validateAndUpdateAISession(AISessionState.submitted)) {
      return;
    }
    var event = createAndMergeEventPayload({
      action: 'submitted',
      actionSubject: 'aiFeedback',
      attributes: {
        aiFeedbackResult: aiFeedbackResult
      }
    }, payload);
    fireEventWithOptionalChannel(event);
  }, [validateAndUpdateAISession, createAndMergeEventPayload, fireEventWithOptionalChannel]);
  return {
    trackAIInteractionInit: trackAIInteractionInit,
    trackAIResultView: trackAIResultView,
    trackAIResultError: trackAIResultError,
    trackAIInteractionDismiss: trackAIInteractionDismiss,
    trackAIResultAction: trackAIResultAction,
    trackAIFeedbackSubmit: trackAIFeedbackSubmit,
    refreshSingleInstrumentationID: refreshSingleInstrumentationID
  };
};